import React from "react";
import { User, Package, BookOpen, AlertCircle, CheckCircle } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "src/components/ui/Card";
import { Label } from "src/components/ui/Label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "src/components/ui/Select";
import { EnrollmentCurriculumScope, EnrollmentProduct, EnrollmentStudent } from "src/services/manualEnrollmentService";

interface EnrollmentScopeSectionProps {
  students: EnrollmentStudent[];
  studentId: string;
  onStudentChange: (id: string) => void;
  products: EnrollmentProduct[];
  productId: string;
  onProductChange: (id: string) => void;
  scopes: EnrollmentCurriculumScope[];
  scopeId: string;
  onScopeChange: (id: string) => void;
  selectedProduct?: EnrollmentProduct;
  objectiveCount?: number;
  domainCount?: number;
}

export const EnrollmentScopeSection: React.FC<EnrollmentScopeSectionProps> = ({
  students,
  studentId,
  onStudentChange,
  products,
  productId,
  onProductChange,
  scopes,
  scopeId,
  onScopeChange,
  selectedProduct,
  objectiveCount,
  domainCount,
}) => {
  const formatScopeLabel = (scope: EnrollmentCurriculumScope) => {
    const board = scope.boards?.board_name || scope.board_id || "Board unspecified";
    const grade = scope.grades?.grade_label || scope.grade_id || "Grade unspecified";
    return `${board} · ${grade} (Version: ${scope.version_label})`;
  };

  const selectedScope = scopes.find((s) => s.id === scopeId);

  return (
    <Card className="border-slate-200/90 shadow-sm overflow-hidden bg-white">
      <CardHeader className="bg-slate-50/60 border-b border-slate-100 py-4 px-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-7 h-7 rounded-lg bg-purple-100 text-purple-700 flex items-center justify-center font-bold text-xs">
              1
            </div>
            <div>
              <CardTitle className="text-sm font-bold text-slate-900">
                Student & Product Scope
              </CardTitle>
              <CardDescription className="text-xs text-slate-500 mt-0.5">
                Link an active student account and lock the curriculum version for this enrollment.
              </CardDescription>
            </div>
          </div>
          <span className="text-[11px] font-mono font-medium text-slate-500 bg-slate-100 border border-slate-200 px-2 py-0.5 rounded">
            STEP 01/03
          </span>
        </div>
      </CardHeader>

      <CardContent className="p-5 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Student Picker */}
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold text-slate-700 flex items-center gap-1.5">
              <User className="w-3.5 h-3.5 text-slate-400" />
              <span>Student Account</span>
              <span className="text-red-500 font-bold">*</span>
            </Label>
            <Select value={studentId} onValueChange={onStudentChange}>
              <SelectTrigger className="w-full text-xs bg-white border-slate-200 rounded-xl shadow-none focus:ring-2 focus:ring-purple-200">
                <SelectValue placeholder="Select student by name or email..." />
              </SelectTrigger>
              <SelectContent className="max-h-60">
                {students.map((student) => {
                  const fullName = `${student.first_name || ""} ${student.last_name || ""}`.trim() || "Unnamed Student";
                  return (
                    <SelectItem key={student.id} value={student.id} className="text-xs">
                      <span className="font-medium text-slate-900">{fullName}</span>
                      <span className="text-slate-400 ml-1.5">({student.email})</span>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
            <p className="text-[11px] text-slate-400">
              Only verified student accounts are eligible for manual activation.
            </p>
          </div>

          {/* Product Picker */}
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold text-slate-700 flex items-center gap-1.5">
              <Package className="w-3.5 h-3.5 text-slate-400" />
              <span>Purchased Product</span>
              <span className="text-red-500 font-bold">*</span>
            </Label>
            <Select value={productId} onValueChange={onProductChange}>
              <SelectTrigger className="w-full text-xs bg-white border-slate-200 rounded-xl shadow-none focus:ring-2 focus:ring-purple-200">
                <SelectValue placeholder="Select active catalog product..." />
              </SelectTrigger>
              <SelectContent className="max-h-60">
                {products.map((item) => (
                  <SelectItem key={item.id} value={item.id} className="text-xs">
                    <span className="font-semibold text-slate-900">{item.name}</span>
                    <span className="ml-2 px-1.5 py-0.5 rounded text-[10px] uppercase font-bold tracking-wide bg-purple-50 text-purple-700 border border-purple-200">
                      {item.type}
                    </span>
                    <span className="ml-2 font-mono text-slate-500">
                      ${item.price.toFixed(2)}/sess
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-[11px] text-slate-400">
              Determines curriculum plan mode: Booster, Custom, or Preparation.
            </p>
          </div>
        </div>

        {/* Published Curriculum Scope */}
        <div className="space-y-1.5 pt-1">
          <div className="flex items-center justify-between">
            <Label className="text-xs font-semibold text-slate-700 flex items-center gap-1.5">
              <BookOpen className="w-3.5 h-3.5 text-slate-400" />
              <span>Published Curriculum Scope</span>
              <span className="text-red-500 font-bold">*</span>
            </Label>
            {selectedScope && (
              <span className="text-[11px] font-mono text-purple-700 bg-purple-50 px-2 py-0.5 rounded border border-purple-200 font-medium">
                Scope ID: {selectedScope.id.slice(0, 8)}...
              </span>
            )}
          </div>

          <Select
            value={scopeId}
            onValueChange={onScopeChange}
            disabled={!selectedProduct || scopes.length === 0}
          >
            <SelectTrigger className="w-full text-xs bg-white border-slate-200 rounded-xl shadow-none focus:ring-2 focus:ring-purple-200 disabled:bg-slate-50 disabled:text-slate-400">
              <SelectValue
                placeholder={
                  !selectedProduct
                    ? "Select a product above first"
                    : scopes.length === 0
                    ? "No published scopes for this subject"
                    : "Select published board, grade, and version label"
                }
              />
            </SelectTrigger>
            <SelectContent className="max-h-60">
              {scopes.map((scope) => (
                <SelectItem key={scope.id} value={scope.id} className="text-xs">
                  {formatScopeLabel(scope)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Contextual Helpers */}
          {selectedProduct && scopes.length === 0 && (
            <div className="flex items-center gap-2 p-2.5 rounded-xl bg-amber-50 border border-amber-200 text-xs text-amber-800">
              <AlertCircle className="w-4 h-4 text-amber-600 shrink-0" />
              <span>No published curriculum scope is available for this product's subject.</span>
            </div>
          )}

          {selectedScope && (
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 p-2.5 rounded-xl bg-emerald-50/70 border border-emerald-200 text-xs text-emerald-800">
              <span className="flex items-center gap-1.5">
                <CheckCircle className="w-4 h-4 text-emerald-600 shrink-0" />
                <span>
                  Scope verified: Version <strong>{selectedScope.version_label}</strong> is locked
                  {objectiveCount && objectiveCount > 0 ? (
                    <> ({objectiveCount} objectives{domainCount && domainCount > 0 ? ` across ${domainCount} domains` : ""})</>
                  ) : null}.
                </span>
              </span>
              <span className="text-[11px] text-emerald-700">Does not alter student profile</span>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

